import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { parseVideoUrl, getEmbedUrl, getThumbnailUrl } from '../utils/songVideos';
import '../styles/song-videos.css';

/**
 * Reference recordings for the loaded song, read from its .meta.json sidecar.
 * Renders nothing at all unless the song actually has links.
 *
 * Collapsing unmounts the list rather than hiding it (which is what the shared
 * CollapsibleSection does, via max-height: 0) — otherwise a playing video would
 * keep making sound from behind a collapsed header.
 */
const SongVideos = ({ videos, onVideoStart }) => {
  const { t } = useLanguage();
  const [collapsed, setCollapsed] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  if (!Array.isArray(videos) || videos.length === 0) return null;

  const handleToggle = () => {
    setCollapsed(prev => {
      // Collapsing tears down any open player
      if (!prev) setActiveIndex(null);
      return !prev;
    });
  };

  const handlePlay = (index) => {
    // Pause the score first so the two don't play over each other
    if (onVideoStart) onVideoStart();
    setActiveIndex(index);
  };

  return (
    <div className="song-videos">
      <div
        className="song-videos-header"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <div className="song-videos-title">
          🎥 {t('songVideos.sectionTitle')}
          <span className="song-videos-count">{videos.length}</span>
        </div>
        <div className={`song-videos-toggle ${!collapsed ? 'expanded' : ''}`}>▼</div>
      </div>

      {!collapsed && (
        <div className="song-videos-content">
          {videos.map((video, index) => {
            const parsed = parseVideoUrl(video.url);
            const embedUrl = getEmbedUrl(parsed);
            const thumbnailUrl = getThumbnailUrl(parsed);
            const isActive = activeIndex === index;
            const label = video.title || `${t('songVideos.videoLabel')} ${index + 1}`;

            return (
              <div className="song-video-item" key={`${video.url}-${index}`}>
                <div className="song-video-label">{label}</div>

                {isActive && embedUrl && (
                  <div className="song-video-frame">
                    <iframe
                      src={embedUrl}
                      title={label}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                {!isActive && embedUrl && (
                  <button
                    type="button"
                    className="song-video-preview"
                    onClick={() => handlePlay(index)}
                    title={t('songVideos.play')}
                    aria-label={`${t('songVideos.play')}: ${label}`}
                  >
                    {thumbnailUrl
                      ? <img src={thumbnailUrl} alt="" loading="lazy" />
                      : <span className="song-video-preview-blank" />}
                    <span className="song-video-play" aria-hidden="true">▶</span>
                  </button>
                )}

                {video.notes && (
                  <div className="song-video-notes">{video.notes}</div>
                )}

                <a
                  className="song-video-external"
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('songVideos.openInNewTab')} ↗
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SongVideos;
